import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { format } from 'date-fns';
//api
import { deletePromoCode } from 'api/invoice';
import { getBookStores } from 'api/company';
import { getPromotionalCodes } from 'api/pathForHook/invoice';
//utils
import useHandleSearch from 'utils/customHooks/useHandleSearch';
import { formatCurrency } from 'utils/currencyFormatter';
//components
import CustomTable from 'components/backoffice/customTable';
import DropdownIcons from 'components/backoffice/dropDownIcons';
import Button from 'components/backoffice/buttonCustom';
import Title from 'components/backoffice/texts/title';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import FiltersTablePromotional from './FiltersTablePromotional';

const PromotionalCodes = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoCode, setPromoCode] = useState({});
  const [formFilter, setFormFilter] = useState({
    search: '',
    active: ''
  })
  const [page, setPage] = useState(1);
  const [responseInfo, setResponseInfo] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { t } = useTranslation(['backoffice']);
  const [companies, setCompanies] = useState([]);
  const [active, setActive] = useState('ninguno');
  const { setTitle } = useOutletContext()

  const colsPromotionalCodes = [
    t('promoCodes.title'),
    t('promoCodes.code'),
    t('promoCodes.startdate'),
    t('promoCodes.enddate'),
    t('promoCodes.discountPercent'),
    t('promoCodes.discountAmount'),
    t('promoCodes.maxRedemptions'),
    t('promoCodes.maxRedemptionsUser'),
    t('promoCodes.libraries'),
    t('activo'),
    t('actions')
  ];

  const { fetchData, stateData, loading } = useHandleSearch();

  const getPromoCodes = async () => {
    try {
      let payload = {
        page,
        page_size: 10,
        search: formFilter.search || '',
        active: formFilter.active || '',
        fields: 'id,title,code,start,end,discount_percent,discount_amount,max_redemptions,max_redemptions_per_user,libraries,active'
      }
      await fetchData(getPromotionalCodes(), payload);
    } catch (error) {
      console.error(error);
    }
  }

  const getCompanies = async () => {
    try {
      const { data, status } = await getBookStores();
      if (data && status === 200) {
        setCompanies(data?.results);
      }
    } catch (error) {
      console.log(error);
      setCompanies([]);
    }
  };

  useEffect(() => {
    getCompanies();
  }, [])

  useEffect(() => {
    setTitle(t('promoCodes.promoCodesTitle'))
    return () => {
      setTitle('');
    };
  }, [setTitle])


  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    if (results !== null) {
      setPromoCodes(results);
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setPromoCodes(null);
      setResponseInfo({ errors });
    }
  }, [stateData])

  useEffect(() => {
    getPromoCodes();
  }, [page, formFilter]);

  const onDelete = async () => {
    const { status } = await deletePromoCode(promoCode.id);
    if (status === 204) {
      toast.success(t('eliminadocorrectamente'));
      setFormFilter({
        active: 'ninguno',
        search: ''
      })
      setShowDeleteModal(false);
    } else {
      toast(`${t('error')} ${status}: ${t('mensajeerror')}`);
    }
  };

  const openModalDelete = (object) => {
    setShowDeleteModal(true);
    setPromoCode(object);
  };

  const handleClear = () => {
    setFormFilter({
      active: 'ninguno',
      search: ''
    })
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }));
  };

  const handleCreate = () => {
    navigate('/intranet/promotionalCodes/create');
  };

  const handleEdit = (id) => {
    navigate(`/intranet/promotionalCodes/edit/${id}`);
  };

  return (
    <div className="d-lg-grid">
      <Title text={t('promoCodes.promoCodesTitle')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col md={9} xs={12} className="d-flex text-start">
          <FiltersTablePromotional
            formFilter={formFilter}
            handleClear={handleClear}
            setFormFilter={setFormFilter}
            t={t}
            page={page}
            setPage={setPage}
          />
        </Col>
        <Col md={3} xs={12} className="d-flex text-end align-bottom p-md-0 justify-content-end pt-5 pt-md-0">
          {userPermissions.includes('promocode.__add') && (
            <Button color="primary" onClick={handleCreate}>
              {t('crear')}
            </Button>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsPromotionalCodes}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {promoCodes?.map((p) => {
            return (
              <tr key={p.id} data-id={p.id}>
                <td className="text-light">{p.title}</td>
                <td className="text-light">{p.code}</td>
                <td className="text-light">{format(new Date(p.start), 'dd/MM/yyyy h:mm aa')}</td>
                <td className="text-light">{format(new Date(p.end), 'dd/MM/yyyy h:mm aa')}</td>
                <td className="text-light">{p.discount_percent}</td>
                <td className="text-light">{formatCurrency(p.discount_amount)}</td>
                <td className="text-light">{p.max_redemptions}</td>
                <td className="text-light">{p.max_redemptions_per_user}</td>
                <td className="text-light">
                  {companies.map((comp) => {
                    if (p.libraries.some((li) => li === comp.id))
                      return (
                        <p key={comp.name} className="libraries-promo-codes">
                          {comp.name}
                        </p>
                      );
                  })}
                </td>
                <td className="text-light">{p.active === false ? 'No' : 'Si'}</td>
                <td className="text-light item-dropdown text-end">
                  <DropdownIcons
                    idItem={p.id}
                    handleDelete={userPermissions.includes('promocode.__delete') && (() => openModalDelete(p))
                    }
                    handleEdit={userPermissions.includes('promocode.__modify') && (() => handleEdit(p.id))}
                  />
                </td>
              </tr>
            );
          })}
        </CustomTable>
      </div>
      <DeleteModalCustom
        openModal={showDeleteModal}
        setOpenModal={setShowDeleteModal}
        handleDelete={onDelete}
        deleteTitle={t('deletepromotionalcode')}
        beforeTitle={t('promoCodes.promoCodesTitle')}
      />
    </div>
  );
};

export default PromotionalCodes;
